import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  TextField
} from '@mui/material';
import { Copy } from 'lucide-react';

const convertImageToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

const ImageBatchBase64Converter = () => {
  const [converted, setConverted] = useState([]);
  const [filter, setFilter] = useState('');

  const handleFiles = async (event) => {
    const files = Array.from(event.target.files);
    const results = [];
    for (const file of files) {
      try {
        const base64 = await convertImageToBase64(file);
        results.push({
          name: file.name,
          path: file.webkitRelativePath || file.name,
          base64
        });
      } catch (e) {
        results.push({
          name: file.name,
          path: file.webkitRelativePath || file.name,
          base64: 'Error converting'
        });
      }
    }
    setConverted(results);
  };

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (e) {
      alert('Failed to copy!');
    }
  };

  const filteredRows = filter
    ? converted.filter(row =>
        row.name.toLowerCase().includes(filter.toLowerCase()) ||
        row.path.toLowerCase().includes(filter.toLowerCase())
      )
    : converted;

  return (
    <Box sx={{ mt: 3, mb: 2 }}>
      <Typography variant="subtitle1" gutterBottom>
        Batch Convert Images to Base64 (for CSV Preparation)
      </Typography>
      <Button
        variant="outlined"
        component="label"
        sx={{ mb: 2 }}
      >
        Select Images
        <input
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={handleFiles}
        />
      </Button>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        You can select multiple images at once. Use the base64 output in your CSV's <b>photoBase64</b> column.
      </Typography>
      <TextField
        label="Filter by name or path"
        size="small"
        value={filter}
        onChange={e => setFilter(e.target.value)}
        sx={{ mb: 2, width: 300 }}
      />
      {filteredRows.length > 0 && (
        <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>File Name</TableCell>
                <TableCell>Path</TableCell>
                <TableCell>Base64 (first 40 chars)</TableCell>
                <TableCell>Copy</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.map((row, idx) => (
                <TableRow key={idx}>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>
                    <Tooltip title={row.path}>
                      <span>{row.path.length > 30 ? row.path.slice(0, 27) + '...' : row.path}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={row.base64}>
                      <span style={{ fontFamily: 'monospace', fontSize: 12 }}>
                        {row.base64.slice(0, 40)}...
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Copy base64 to clipboard">
                      <IconButton
                        size="small"
                        onClick={() => handleCopy(row.base64)}
                        color="primary"
                      >
                        <Copy size={16} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

const App = () => {
  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
      <Button variant="contained" component="label">
        Import from CSV
        <input
          type="file"
          accept=".csv"
          hidden
          // onChange={handleCSVImport}
        />
      </Button>
      <ImageBatchBase64Converter />
    </Box>
  );
};

export default App;